import type { Stripe } from "@stripe/stripe-js";
// Use loadStripe from @stripe/stripe-js/pure instead of @stripe/stripe-js as we
// do not need to load the Stripe.js script tag immediately (that's already
// handled by pages/_app.tsx, from the "dahlia" release train - see pure.js's
// own findScript/RELEASE_TRAIN)
import { loadStripe } from "@stripe/stripe-js/pure";
import { useEffect, useState } from "react";

import { assertEnvPresent } from "@every.org/common/src/helpers/getEnv";

// Module-scoped so every caller shares one Stripe.js instance instead of each
// useStripePromise()/useStripeInstance() call loading its own (loadStripe()
// itself only memoizes the underlying script tag, not the Stripe instance).
let stripePromise: Promise<Stripe | null> | undefined;

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    // No apiVersion option here - the loaded script is already pinned to a
    // specific API version by its release train (see pages/_app.tsx), and
    // Stripe.js throws ("Can not provide apiVersion to Stripe()") if you try
    // to also pass one at construction time.
    stripePromise = loadStripe(
      assertEnvPresent(
        process.env.NEXT_PUBLIC_STRIPE_PUBLIC_API_KEY ||
          process.env.REACT_APP_STRIPE_PUBLIC_API_KEY,
        "STRIPE_PUBLIC_API_KEY"
      ),
      {
        // Stripe.js shows a floating "Stripe Developer Tools" sandbox
        // assistant whenever Elements are mounted with a test key. It sits
        // over the bottom of the page and intercepts clicks in Playwright
        // (the header popover's Logout link, for one), and since the wallet
        // availability probe (PaymentRequestContext) keeps an Elements group
        // mounted for the rest of the session, it never goes away on its own.
        developerTools: { assistant: { enabled: false } },
      }
    );
  }
  return stripePromise;
}

/**
 * The Promise<Stripe | null> to pass as <Elements stripe={...}>. Only needed
 * where a PaymentElement/SetupIntent form is actually rendered - everywhere
 * else, use useStripeInstance() instead so an <Elements> provider isn't
 * required.
 */
export function useStripePromise(): Promise<Stripe | null> | undefined {
  const [promise, setPromise] = useState<Promise<Stripe | null>>();
  useEffect(() => {
    setPromise(getStripePromise());
  }, []);
  return promise;
}

/**
 * The resolved Stripe instance directly, for call sites that only need raw
 * stripe.* methods (confirmCardPayment, paymentRequest, etc.) and don't
 * render any Elements/PaymentElement - so they don't need an <Elements>
 * provider ancestor the way useStripe() from @stripe/react-stripe-js does.
 */
export function useStripeInstance(): Stripe | null {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  useEffect(() => {
    let cancelled = false;
    getStripePromise().then((resolved) => {
      if (!cancelled) {
        setStripe(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return stripe;
}
